import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import api from "api";
import PageHeader from "components/layout/page-header/PageHeader";
import { formatBytes } from "./utils/formatters";
import styles from "./ProjectRegistrationAdm.module.css";
import AppModal from "components/ui/AppModal/AppModal";
import { clearProjectCache } from "utils/authUtils";

const PRODUCTS = [
  { value: "fotos",         label: "Fotos",         icon: "fa-camera" },
  { value: "filmagens",     label: "Filmagens",      icon: "fa-video" },
  { value: "gemeo_digital", label: "Gêmeo Digital",  icon: "fa-cube" },
  { value: "tour_360",      label: "Tour 360°",      icon: "fa-street-view" },
  { value: "geoview",       label: "GeoView",        icon: "fa-map" },
  { value: "bim",           label: "BIM",            icon: "fa-building" },
];

const EMPTY_PROJECTS = {
  fotos: [], filmagens: [], gemeo_digital: [], tour_360: [], geoview: [], bim: []
};

const todayISO = () => new Date().toISOString().split("T")[0];

function initials(username = "") {
  const parts = username.split(/[\s._@-]/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return username.slice(0, 2).toUpperCase() || "?";
}

function ProjectRegistrationAdm({ token, email }) {
  // ── Client selection ──
  const [clientes, setClientes]           = useState([]);
  const [cliente, setCliente]             = useState("");
  const [clienteEmail, setClienteEmail]   = useState("");
  const [clienteSearch, setClienteSearch] = useState("");
  const [showDropdown, setShowDropdown]   = useState(false);
  const [dropdownRect, setDropdownRect]   = useState(null);
  const clienteRef = useRef(null);

  // ── Project details ──
  const [projectMode, setProjectMode]           = useState("new"); // "new" | "existing"
  const [nome, setNome]                         = useState("");
  const [produto, setProduto]                   = useState("");
  const [dataProjeto, setDataProjeto]           = useState(todayISO());
  const [projetosExistentes, setProjetosExistentes] = useState(EMPTY_PROJECTS);
  const [projetoExistente, setProjetoExistente] = useState("");

  // ── Files ──
  const [arquivos, setArquivos]   = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // ── Submit state ──
  const [uploadProgress, setUploadProgress]     = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [mensagemErro, setMensagemErro]         = useState("");
  const [loadingProjects, setLoadingProjects]   = useState(false);

  // ── Modal ──
  const [modal, setModal] = useState({ open: false, variant: "custom", title: "", message: "", onConfirm: null });
  const modalResolveRef = useRef(null);

  const openModal = (config) =>
    new Promise(resolve => {
      modalResolveRef.current = resolve;
      setModal({ ...config, open: true });
    });

  const closeModal = () => {
    setModal(m => ({ ...m, open: false }));
    modalResolveRef.current?.();
    modalResolveRef.current = null;
  };

  // Fetch clientes list
  useEffect(() => {
    if (!token) return;
    api.get("/users/all", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setClientes(res.data))
      .catch(() => {});
  }, [token]);

  // Position dropdown portal on show
  useEffect(() => {
    if (!showDropdown || !clienteRef.current) return;
    const rect = clienteRef.current.getBoundingClientRect();
    setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [showDropdown]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => {
      const portal = document.getElementById("adm-client-dropdown");
      if (portal?.contains(e.target)) return;
      if (clienteRef.current?.contains(e.target)) return;
      setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch existing projects for selected client
  useEffect(() => {
    if (!cliente) { setProjetosExistentes(EMPTY_PROJECTS); return; }
    setLoadingProjects(true);
    api.get("/admin/user", {
      headers: { Authorization: `Bearer ${token}` },
      params: { username: cliente }
    }).then(res => {
      const data = res.data;
      const projetos = { fotos: [], filmagens: [], gemeo_digital: [], tour_360: [], geoview: [], bim: [] };
      for (const [product, nameMap] of Object.entries(data)) {
        const normalizedProduct =
          product === "geotiff" || product === "ortomosaico" ? "geoview" : product;
        if (!projetos[normalizedProduct]) continue;
        for (const [name, dates] of Object.entries(nameMap || {})) {
          const datesArr = Array.isArray(dates) ? dates : [];
          datesArr.forEach(date => projetos[normalizedProduct].push({ name, product: normalizedProduct, date }));
        }
      }
      setProjetosExistentes(projetos);
    }).catch(err => {
      console.error("Erro ao buscar projetos do cliente:", err);
    }).finally(() => {
      setLoadingProjects(false);
    });
  }, [cliente, token]);

  const handleSelectCliente = cli => {
    setCliente(cli.username);
    setClienteEmail(cli.email || "");
    setClienteSearch(cli.username);
    setShowDropdown(false);
    setDropdownRect(null);
    resetForm();
  };

  const handleClearCliente = () => {
    setCliente("");
    setClienteEmail("");
    setClienteSearch("");
    setShowDropdown(false);
    resetForm();
  };

  const resetForm = () => {
    setNome("");
    setProduto("");
    setDataProjeto(todayISO());
    setProjetoExistente("");
    setArquivos([]);
    setMensagemErro("");
    setUploadProgress(null);
    setProjectMode("new");
  };

  const handleProjetoExistenteChange = e => {
    const value = e.target.value;
    setProjetoExistente(value);
    if (!value) { setNome(""); setProduto(""); setDataProjeto(todayISO()); return; }
    const allProjects = Object.values(projetosExistentes).flat();
    const proj = allProjects.find(p => `${p.name}||${p.product}||${p.date}` === value);
    if (proj) { setNome(proj.name); setProduto(proj.product); setDataProjeto(proj.date); }
  };

  const handleSwitchMode = mode => {
    setProjectMode(mode);
    setNome("");
    setProduto("");
    setDataProjeto(todayISO());
    setProjetoExistente("");
    setMensagemErro("");
  };

  // ── File handling ──
  const handleFiles = newFiles => {
    if (!newFiles?.length) return;
    const toAdd = [];
    for (const f of newFiles) {
      if (!arquivos.some(a => a.name === f.name)) toAdd.push(f);
    }
    if (toAdd.length < newFiles.length) {
      setMensagemErro("Alguns arquivos já foram selecionados e foram ignorados.");
    } else {
      setMensagemErro("");
    }
    if (toAdd.length) setArquivos(prev => [...prev, ...toAdd]);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    if (loading) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = idx => setArquivos(prev => prev.filter((_, i) => i !== idx));

  const totalSize = arquivos.reduce((s, f) => s + f.size, 0);

  // ── Submit ──
  const handleSubmit = async e => {
    e.preventDefault();
    if (!cliente)     { setMensagemErro("Selecione um cliente.");            return; }
    if (!nome)        { setMensagemErro("Informe o nome do projeto.");        return; }
    if (!produto)     { setMensagemErro("Selecione o tipo de produto.");      return; }
    if (!dataProjeto) { setMensagemErro("Informe a data do projeto.");        return; }
    if (!arquivos.length) { setMensagemErro("Adicione pelo menos um arquivo."); return; }

    setLoading(true);
    setUploadProgress(0);
    setMensagemErro("");

    const formData = new FormData();
    formData.append("user", cliente);
    formData.append("name", nome);
    formData.append("product", produto);
    formData.append("date", dataProjeto);
    arquivos.forEach(f => formData.append("files", f));

    // ⚠️ NUNCA defina Content-Type manualmente com FormData —
    // o navegador adiciona automaticamente o boundary correto.
    try {
      const response = await api.post("/admin/create", formData, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: event => {
          if (!event.total) return;
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      });

      if (response.status === 201) {
        const isProcessing =
          response.data?.status === "processing" ||
          response.data?.message?.includes("segundo plano");

        await openModal({
          variant: "success",
          title: isProcessing ? "Processamento iniciado!" : "Projeto criado!",
          message: isProcessing
            ? "Os arquivos estão sendo processados em segundo plano."
            : "Arquivos enviados com sucesso.",
          onConfirm: closeModal,
        });

        clearProjectCache(email);

        resetForm();
      }
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Erro ao enviar o projeto. Tente novamente.";
      await openModal({ variant: "error", title: "Erro", message: msg, onConfirm: closeModal });
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const filteredClientes = clientes
    .filter(c => {
      const q = clienteSearch.toLowerCase();
      return (
        c.username.toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.username.localeCompare(b.username));

  const allExisting = Object.values(projetosExistentes).flat();
  const canSubmit = !loading && !!nome && !!produto && !!dataProjeto && arquivos.length > 0;

  return (
    <div className={styles.pageContainer}>
      <PageHeader
        title="Editar Projetos"
        subtitle="Inclua ou altere projetos dos clientes."
        accent="Administração"
      />

      <div className={styles.content}>

        {/* ── Client selector ── */}
        <div className={styles.clientCard}>
          <p className={styles.clientCardTitle}>Cliente</p>

          {cliente ? (
            <div
              className={styles.clientSelected}
              onClick={handleClearCliente}
              title="Clique para trocar de cliente"
            >
              <div className={styles.clientAvatar}>{initials(cliente)}</div>
              <div className={styles.clientSelectedInfo}>
                <div className={styles.clientSelectedName}>{cliente}</div>
                {clienteEmail && (
                  <div className={styles.clientSelectedSub}>
                    {clienteEmail} · clique para trocar
                  </div>
                )}
              </div>
              <i className="fas fa-times" style={{ color: "#94a3b8", fontSize: "0.8rem" }} />
            </div>
          ) : (
            <div ref={clienteRef} className={styles.clientSearchWrap}>
              <i className={`fas fa-search ${styles.clientSearchIcon}`} />
              <input
                type="text"
                className={styles.clientInput}
                placeholder="Buscar por nome ou e-mail..."
                value={clienteSearch}
                onChange={e => { setClienteSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                autoComplete="off"
              />
            </div>
          )}

          {showDropdown && dropdownRect && !cliente && createPortal(
            <div
              id="adm-client-dropdown"
              className={`${styles.dropdown} ${styles.dropdownFixed}`}
              style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
            >
              {filteredClientes.length === 0 ? (
                <div className={styles.dropdownEmpty}>Nenhum cliente encontrado</div>
              ) : filteredClientes.map(cli => (
                <div
                  key={cli.id}
                  className={styles.dropdownItem}
                  onMouseDown={() => handleSelectCliente(cli)}
                >
                  <div className={styles.dropdownAvatar}>{initials(cli.username)}</div>
                  <div className={styles.dropdownItemInfo}>
                    <div className={styles.dropdownItemName}>{cli.username}</div>
                    {cli.email && (
                      <div className={styles.dropdownItemEmail}>{cli.email}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )}
        </div>

        {/* ── Project form ── */}
        <form
          onSubmit={handleSubmit}
          className={`${styles.formCard} ${!cliente ? styles.formCardDisabled : ""}`}
          noValidate
        >
          <div className={styles.formBody}>

            {/* Project mode toggle */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Modo</label>
              <div className={styles.modeToggle}>
                <button
                  type="button"
                  className={`${styles.modeBtn} ${projectMode === "new" ? styles.modeBtnActive : ""}`}
                  onClick={() => handleSwitchMode("new")}
                >
                  <i className="fas fa-plus" style={{ marginRight: 5 }} />
                  Novo projeto
                </button>
                <button
                  type="button"
                  className={`${styles.modeBtn} ${projectMode === "existing" ? styles.modeBtnActive : ""}`}
                  onClick={() => handleSwitchMode("existing")}
                >
                  <i className={`fas ${loadingProjects ? "fa-spinner fa-spin" : "fa-folder-open"}`} style={{ marginRight: 5 }} />
                  Projeto existente
                  {allExisting.length > 0 && (
                    <span style={{ marginLeft: 4, opacity: 0.7 }}>({allExisting.length})</span>
                  )}
                </button>
              </div>
            </div>

            {/* Project name / existing select */}
            {projectMode === "existing" ? (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Selecione o projeto</label>
                <select
                  className={styles.formControl}
                  value={projetoExistente}
                  onChange={handleProjetoExistenteChange}
                  disabled={loading || loadingProjects}
                >
                  <option value="">
                    {loadingProjects ? "Carregando..." : allExisting.length === 0 ? "Nenhum projeto encontrado" : "— Selecione —"}
                  </option>
                  {PRODUCTS.map(p => {
                    const list = projetosExistentes[p.value] || [];
                    if (!list.length) return null;
                    return (
                      <optgroup key={p.value} label={p.label}>
                        {list.map(proj => (
                          <option
                            key={`${proj.name}-${proj.date}`}
                            value={`${proj.name}||${proj.product}||${proj.date}`}
                          >
                            {proj.name} · {proj.date}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              </div>
            ) : (
              <div className={styles.formGroup}>
                <label htmlFor="adm-nome" className={styles.formLabel}>Nome do projeto</label>
                <input
                  id="adm-nome"
                  type="text"
                  className={styles.formControl}
                  placeholder="Digite o nome do projeto"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  disabled={loading}
                  autoComplete="off"
                />
              </div>
            )}

            {/* Produto + Data */}
            <div className={styles.twoCol}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Tipo de produto</label>
                <div className={styles.productGrid}>
                  {PRODUCTS.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      className={`${styles.productCard} ${produto === p.value ? styles.productCardActive : ""}`}
                      onClick={() => setProduto(p.value)}
                      disabled={loading}
                    >
                      <i className={`fas ${p.icon}`} />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="adm-date" className={styles.formLabel}>Data do projeto</label>
                <input
                  id="adm-date"
                  type="date"
                  className={styles.formControl}
                  value={dataProjeto}
                  onChange={e => setDataProjeto(e.target.value)}
                  disabled={loading || (projectMode === "existing" && !!projetoExistente)}
                />
              </div>
            </div>

            {/* File upload */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Arquivos</label>
              <div
                className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ""} ${loading ? styles.dropZoneDisabled : ""}`}
                onDragOver={e => { e.preventDefault(); if (!loading) setIsDragging(true); }}
                onDragEnter={e => { e.preventDefault(); if (!loading) setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => !loading && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={e => { handleFiles(Array.from(e.target.files)); e.target.value = ""; }}
                />
                <i className={`fas fa-cloud-upload-alt ${styles.dropZoneIcon}`} />
                <p className={styles.dropZoneText}>
                  {isDragging ? "Solte os arquivos aqui" : "Arraste arquivos ou clique para selecionar"}
                </p>
              </div>

              {arquivos.length > 0 && (
                <>
                  <div className={styles.fileMetaRow}>
                    <span className={styles.fileUploadMeta}>
                      {arquivos.length} arquivo(s) — {formatBytes(totalSize)}
                    </span>
                    <button
                      type="button"
                      className={styles.buttonOutline}
                      style={{ padding: "0.2rem 0.6rem", fontSize: "0.72rem", borderRadius: "6px" }}
                      onClick={() => setArquivos([])}
                    >
                      Limpar tudo
                    </button>
                  </div>
                  <ul className={styles.fileList}>
                    {arquivos.map((f, i) => (
                      <li key={`${f.name}-${i}`} className={styles.fileListItem}>
                        <i className="fas fa-file text-muted" style={{ fontSize: "0.8rem", flexShrink: 0 }} />
                        <span title={f.name}>{f.name}</span>
                        <span className={styles.fileListMeta}>{formatBytes(f.size)}</span>
                        <button
                          type="button"
                          className={styles.fileRemoveButton}
                          onClick={() => removeFile(i)}
                          title={`Remover ${f.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {mensagemErro && <p className={styles.errorMessage}>{mensagemErro}</p>}
            </div>
          </div>

          {/* ── Actions ── */}
          <div className={styles.formActions}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonOutline}`}
              onClick={resetForm}
              disabled={loading}
            >
              <i className="fas fa-undo" /> Limpar
            </button>

            <button
              type="submit"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={!canSubmit}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane" /> Enviar projeto
                </>
              )}
            </button>

            {uploadProgress !== null && (
              <div className={styles.uploadProgressWrap}>
                <div className={styles.uploadProgressLabel}>
                  <span>Upload</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className={styles.uploadProgressBar}>
                  <div className={styles.uploadProgressFill} style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* ── Modal unificado ───────────────────────────────────────────── */}
      <AppModal
        isOpen={modal.open}
        onClose={closeModal}
        allowOutsideClick={true}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        confirmText="OK"
        onConfirm={modal.onConfirm}
      />
    </div>
  );
}

const mapStateToProps = state => ({
  token: state.auth.accessToken,
  email: state.auth.email
});
export default connect(mapStateToProps)(ProjectRegistrationAdm);
